import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ── Theme colours (matching app CSS variables, default girl theme) ──────────
const C_PINK_DARK  = [255, 133, 192]; // #FF85C0 --primary-dark
const C_PINK_SOFT  = [255, 179, 217]; // #FFB3D9 --primary-soft
const C_PINK_LIGHT = [255, 243, 251]; // very light pink for alternating rows
const C_TEAL       = [77,  208, 225]; // #4DD0E1 --secondary-dark
const C_TEXT_DARK  = [66,  66,  66];  // #424242
const C_TEXT_LIGHT = [117, 117, 117]; // #757575
const C_WHITE      = [255, 255, 255];
const C_BORDER     = [224, 224, 224]; // #E0E0E0

const PAGE_W  = 210; // A4 mm
const PAGE_H  = 297;
const MARGIN  = 14;
const HEADER_H = 24;

// Converts { label, key, formatter? } → autoTable { header, dataKey }
const toAutoColumns = (cols) => cols.map((c) => ({ header: c.label, dataKey: c.key }));

// Formats one data row using column configs (same logic as CSV export)
const formatRow = (item, cols) => {
  const row = {};
  cols.forEach((c) => {
    const raw = item[c.key] ?? "";
    row[c.key] = c.formatter
      ? c.formatter(raw)
      : String(raw === null || raw === undefined ? "" : raw);
  });
  return row;
};

// Draws the pink themed header band that appears at the top of every data page
const drawPageHeader = (doc, subtitle, childName) => {
  doc.setFillColor(...C_PINK_DARK);
  doc.rect(0, 0, PAGE_W, HEADER_H, "F");

  // Teal accent line under the header
  doc.setFillColor(...C_TEAL);
  doc.rect(0, HEADER_H, PAGE_W, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...C_WHITE);
  doc.text("Baby Tracker", MARGIN, 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(subtitle, MARGIN, 19);

  if (childName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(childName, PAGE_W - MARGIN, 10, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(245, 220, 240);
    doc.text(new Date().toLocaleDateString(), PAGE_W - MARGIN, 19, { align: "right" });
  }
};

// Page footer callback passed to autoTable's didDrawPage
const makeFooter = (doc) => (hookData) => {
  doc.setFontSize(7);
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text(
    `Page ${hookData.pageNumber}`,
    PAGE_W / 2,
    PAGE_H - 6,
    { align: "center" }
  );
};

// Shared autoTable styles used across all tables
const TABLE_STYLES = {
  headStyles: {
    fillColor: C_PINK_DARK,
    textColor: C_WHITE,
    fontStyle: "bold",
    fontSize: 8,
    cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
  },
  alternateRowStyles: { fillColor: C_PINK_LIGHT },
  bodyStyles: {
    fontSize: 8,
    cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
    textColor: C_TEXT_DARK,
    lineColor: C_BORDER,
    lineWidth: 0.05,
  },
  tableLineColor: C_BORDER,
  tableLineWidth: 0.1,
};

// ────────────────────────────────────────────────────────────────────────────

/**
 * Exports a single section of data as a standalone themed PDF.
 *
 * @param {Object[]} data           - Array of data records
 * @param {string}   filename       - Output filename (e.g. "bottle-feeds-Oliver.pdf")
 * @param {Array}    columns        - Column config: { label, key, formatter? }
 * @param {string}   sectionTitle   - Human-readable section name shown in the header
 * @param {string}   [childName]    - Child name shown in the header
 */
export const exportSectionPDF = (data, filename, columns, sectionTitle, childName = "") => {
  if (!data || data.length === 0) {
    alert("No data to export.");
    return;
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  drawPageHeader(doc, sectionTitle, childName);

  // Record count sub-label
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text(
    `${data.length} record${data.length !== 1 ? "s" : ""}`,
    MARGIN,
    HEADER_H + 8
  );

  autoTable(doc, {
    columns: toAutoColumns(columns),
    body: data.map((item) => formatRow(item, columns)),
    startY: HEADER_H + 12,
    margin: { left: MARGIN, right: MARGIN },
    ...TABLE_STYLES,
    didDrawPage: makeFooter(doc),
  });

  doc.save(filename);
};

/**
 * Exports a complete multi-section activity report as a single PDF.
 * Sections with no data are silently skipped.
 *
 * @param {Array<{ title: string, data: Object[], columns: Object[] }>} sections
 * @param {string} childName
 * @param {string} [childDOB]  - ISO date string or "YYYY-MM-DD"
 */
export const exportFullReportPDF = (sections, childName = "Child", childDOB = "") => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const generated = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  const nonEmpty = sections.filter((s) => s.data?.length > 0);
  const totalRecords = sections.reduce((sum, s) => sum + (s.data?.length || 0), 0);

  // ── Cover page ─────────────────────────────────────────────────────────────
  // Pink gradient band
  doc.setFillColor(...C_PINK_SOFT);
  doc.rect(0, 0, PAGE_W, 78, "F");
  doc.setFillColor(...C_TEAL);
  doc.rect(0, 78, PAGE_W, 3, "F");

  // App title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...C_WHITE);
  doc.text("Baby Tracker", PAGE_W / 2, 30, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  doc.text("Full Activity Report", PAGE_W / 2, 43, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(255, 240, 248);
  doc.text(`Generated ${generated}`, PAGE_W / 2, 55, { align: "center" });

  // Child info card
  const cardY = 90;
  const cardH = 44;
  doc.setFillColor(...C_WHITE);
  doc.roundedRect(MARGIN, cardY, PAGE_W - MARGIN * 2, cardH, 3, 3, "F");
  doc.setDrawColor(...C_BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, cardY, PAGE_W - MARGIN * 2, cardH, 3, 3, "S");

  // Child name (left side)
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text("Child", MARGIN + 8, cardY + 10);
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_TEXT_DARK);
  doc.text(childName, MARGIN + 8, cardY + 20);

  if (childDOB) {
    const dobStr = (() => {
      try {
        return new Date(childDOB).toLocaleDateString("en-US", {
          year: "numeric", month: "long", day: "numeric",
        });
      } catch {
        return childDOB;
      }
    })();
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_TEXT_LIGHT);
    doc.text("Date of Birth", MARGIN + 8, cardY + 31);
    doc.setFontSize(10);
    doc.setTextColor(...C_TEXT_DARK);
    doc.text(dobStr, MARGIN + 8, cardY + 39);
  }

  // Total records (right side)
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_PINK_DARK);
  doc.text(String(totalRecords), PAGE_W - MARGIN - 8, cardY + 20, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text("total records", PAGE_W - MARGIN - 8, cardY + 28, { align: "right" });
  doc.text(`across ${nonEmpty.length} section${nonEmpty.length !== 1 ? "s" : ""}`, PAGE_W - MARGIN - 8, cardY + 36, { align: "right" });

  // Contents list
  const tocY = cardY + cardH + 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_TEXT_DARK);
  doc.text("Contents", MARGIN, tocY);
  doc.setDrawColor(...C_PINK_DARK);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, tocY + 2, MARGIN + 28, tocY + 2);

  nonEmpty.forEach((s, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = MARGIN + col * 62;
    const y = tocY + 11 + row * 9;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_PINK_DARK);
    doc.text(s.title, x, y);
    doc.setTextColor(...C_TEXT_LIGHT);
    doc.text(` (${s.data.length})`, x + doc.getTextWidth(s.title), y);
  });

  // ── One page per section ───────────────────────────────────────────────────
  nonEmpty.forEach((section) => {
    doc.addPage();
    drawPageHeader(doc, section.title, childName);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_TEXT_LIGHT);
    doc.text(
      `${section.data.length} record${section.data.length !== 1 ? "s" : ""}`,
      MARGIN,
      HEADER_H + 8
    );

    autoTable(doc, {
      columns: toAutoColumns(section.columns),
      body: section.data.map((item) => formatRow(item, section.columns)),
      startY: HEADER_H + 12,
      margin: { left: MARGIN, right: MARGIN },
      ...TABLE_STYLES,
      didDrawPage: makeFooter(doc),
    });
  });

  const slug = childName.toLowerCase().replace(/\s+/g, "-");
  doc.save(`baby-tracker-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ─────────────────────────────────────────────────────────────────────────────

const APPT_LABELS = {
  "gp":             "GP",
  "health-visitor": "Health Visitor",
  "check-6week":    "6-Week Check",
  "check-8month":   "8-Month Review",
  "check-1year":    "1-Year Review",
  "check-2year":    "2-Year Review",
  "ae":             "A&E",
  "specialist":     "Specialist",
  "other":          "Other",
};

/**
 * Exports a narrative-style Doctor Visit Report PDF.
 * Each visit is rendered as a labelled card rather than a plain table row.
 */
export const exportDoctorVisitReportPDF = (visits, childName = "Child", childDOB = "") => {
  if (!visits || visits.length === 0) {
    alert("No visits to export.");
    return;
  }

  const doc     = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const sorted  = [...visits].sort((a, b) => new Date(a.date) - new Date(b.date));
  const toGB    = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return iso; }
  };

  // ── Cover page ─────────────────────────────────────────────────────────────
  doc.setFillColor(...C_PINK_SOFT);
  doc.rect(0, 0, PAGE_W, 78, "F");
  doc.setFillColor(...C_TEAL);
  doc.rect(0, 78, PAGE_W, 3, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...C_WHITE);
  doc.text("Baby Tracker", PAGE_W / 2, 28, { align: "center" });
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text("Doctor Visit Report", PAGE_W / 2, 40, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(255, 240, 248);
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`,
    PAGE_W / 2, 53, { align: "center" }
  );

  // Child info card
  const cardY = 90;
  doc.setFillColor(...C_WHITE);
  doc.roundedRect(MARGIN, cardY, PAGE_W - MARGIN * 2, 42, 3, 3, "F");
  doc.setDrawColor(...C_BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, cardY, PAGE_W - MARGIN * 2, 42, 3, 3, "S");

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text("Child", MARGIN + 8, cardY + 11);
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_TEXT_DARK);
  doc.text(childName, MARGIN + 8, cardY + 21);

  if (childDOB) {
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_TEXT_LIGHT);
    doc.text("Date of Birth", MARGIN + 8, cardY + 30);
    doc.setFontSize(10);
    doc.setTextColor(...C_TEXT_DARK);
    doc.text(toGB(childDOB), MARGIN + 8, cardY + 38);
  }

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_PINK_DARK);
  doc.text(String(sorted.length), PAGE_W - MARGIN - 8, cardY + 21, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_TEXT_LIGHT);
  doc.text(`visit${sorted.length !== 1 ? "s" : ""} recorded`, PAGE_W - MARGIN - 8, cardY + 29, { align: "right" });

  if (sorted.length > 0) {
    const first = toGB(sorted[0].date);
    const last  = toGB(sorted[sorted.length - 1].date);
    doc.text(first === last ? first : `${first} – ${last}`, PAGE_W - MARGIN - 8, cardY + 37, { align: "right" });
  }

  // ── Visit pages ────────────────────────────────────────────────────────────
  doc.addPage();
  drawPageHeader(doc, "Doctor Visit Report", childName);

  // Build a flat body array — visit header rows + data rows + spacers
  const body     = [];
  const rowTypes = [];

  sorted.forEach((v, i) => {
    const typeLabel      = APPT_LABELS[v.appointmentType] || v.appointmentType;
    const hasMeasure     = v.weight || v.height || v.headCircumference;

    // ── Visit header row ──────────────────────────────────────────────────
    rowTypes.push("header");
    body.push({ field: typeLabel, value: toGB(v.date) });

    // Reason (always shown)
    rowTypes.push("row");
    body.push({ field: "Reason for Visit", value: v.reason || "—" });

    if (v.practitioner) {
      rowTypes.push("row");
      body.push({ field: "Practitioner", value: v.practitioner });
    }

    if (hasMeasure) {
      const parts = [];
      if (v.weight)            parts.push(`Weight: ${v.weight} kg`);
      if (v.height)            parts.push(`Height: ${v.height} cm`);
      if (v.headCircumference) parts.push(`HC: ${v.headCircumference} cm`);
      rowTypes.push("row");
      body.push({ field: "Measurements", value: parts.join("   ·   ") });
    }

    if (v.notes) {
      rowTypes.push("row");
      body.push({ field: "Outcome / Notes", value: v.notes });
    }

    if (v.prescriptions) {
      rowTypes.push("row");
      body.push({ field: "Prescriptions", value: v.prescriptions });
    }

    if (v.followUpDate) {
      rowTypes.push("followup");
      body.push({ field: "Follow-up Date", value: toGB(v.followUpDate) });
    }

    // Spacer between visits
    if (i < sorted.length - 1) {
      rowTypes.push("spacer");
      body.push({ field: " ", value: " " });
    }
  });

  autoTable(doc, {
    startY:    HEADER_H + 8,
    margin:    { left: MARGIN, right: MARGIN },
    showHead:  "never",
    columns:   [
      { header: "", dataKey: "field" },
      { header: "", dataKey: "value" },
    ],
    body,
    columnStyles: {
      field: { cellWidth: 44, fontStyle: "bold", fontSize: 7.5, textColor: C_TEXT_LIGHT },
      value: { fontSize: 8.5, textColor: C_TEXT_DARK },
    },
    styles: {
      overflow:    "linebreak",
      cellPadding: { top: 3.5, right: 5, bottom: 3.5, left: 5 },
      lineColor:   C_BORDER,
      lineWidth:   0.05,
    },
    didParseCell: (data) => {
      const type = rowTypes[data.row.index];
      if (type === "header") {
        data.cell.styles.fillColor   = C_PINK_DARK;
        data.cell.styles.textColor   = C_WHITE;
        data.cell.styles.fontStyle   = "bold";
        data.cell.styles.fontSize    = 9;
        data.cell.styles.cellPadding = { top: 5, right: 6, bottom: 5, left: 6 };
      } else if (type === "followup" && data.column.dataKey === "value") {
        data.cell.styles.textColor = C_PINK_DARK;
        data.cell.styles.fontStyle = "bold";
      } else if (type === "spacer") {
        data.cell.styles.fillColor     = [248, 248, 248];
        data.cell.styles.cellPadding   = { top: 2, right: 0, bottom: 2, left: 0 };
        data.cell.styles.lineWidth     = 0;
        data.cell.styles.minCellHeight = 4;
        data.cell.styles.fontSize      = 4;
      }
    },
    didDrawPage: makeFooter(doc),
  });

  const slug = childName.toLowerCase().replace(/\s+/g, "-");
  doc.save(`doctor-visits-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
};
